import { Box, Grid2 } from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid, Modal, Toast } from "../common";
import {
  InvoiceFilters,
  InvoiceInfo,
  InvoiceItem,
  InvoiceOverviewInfo,
  InvoiceRequestParams,
  ToastOptions,
} from "../../types";
import {
  buildQueryString,
  downloadFile,
  formatCurrency,
  getInvoiceDetailsRoute,
  getMultipleCheckPaymentsRoute,
  theme,
} from "../../utils";
import { useAtom } from "jotai";
import {
  invoiceDataGridPaginationAtom,
  invoiceFilterStateAtom,
  isInvoiceActionsMutationsLoadingAtom,
  isReceivePaymentFormActiveAtom,
  selectedAccountAtom,
  selectedAccountIdAtom,
  selectedOrgIdAtom,
  userAtom,
} from "../../atoms";
import {
  useFetchDownloadInvoicesCsvQuery,
  useFetchInvoiceOverviewQuery,
  useFetchInvoicesQuery,
  useRegenerateInvoiceMutation,
} from "../../api/invoiceService";
import { GridColDef, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { formatDate, getDateRange } from "../../utils/dateUtil";
import { InvoiceActionIcons, InvoiceToolbar } from "./fragments";
import {
  DateRangeOptions,
  InvoicePaymentCallbackMessage,
  InvoiceStatus,
  Role,
  InvoicePaymentStatus,
} from "../../enums";
import { CreateInvoice } from "./CreateInvoice";
import { queryClient } from "../../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ACCOUNT_DETAILS_TAB_KEY,
  CUSTOMER_INVOICE_STATUS_FILTER_DROPDOWN,
  STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_TILES,
} from "../../constants";
import { useAttachPdfToQuickbooksMutation } from "../../api";
import { CreateRegenerateInvoiceModal } from "../common/modal/CreateRegenerateInvoiceModal";
import { PaymentDialog } from "../payment";
import { Payment as PaymentIcon } from "@mui/icons-material";

export const Invoice = () => {
  const navigate = useNavigate();
  const [selectedDateRange, setSelectedDateRange] =
    useState<string>("last12Months");
  const [queryParams, setQueryParams] = useSearchParams();
  const [user] = useAtom(userAtom);
  const [filterValues, setFilterValues] = useAtom(invoiceFilterStateAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [selectedAccountId] = useAtom(selectedAccountIdAtom);
  const [selectedAccount] = useAtom(selectedAccountAtom);
  const [invoiceList, setInvoiceList] = useState<InvoiceInfo>();
  const [isInvoiceActionsMutationsLoading] = useAtom(
    isInvoiceActionsMutationsLoadingAtom,
  );
  const [showMobilePaymentStatusMenu, setShowMobilePaymentStatusMenu] =
    useState(true);
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] =
    useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [orgId, setOrgId] = useState(0);
  const [accountId, setAccountId] = useState(0);
  const [invoicePaginationModel, setInvoicePaginationModel] = useAtom(
    invoiceDataGridPaginationAtom,
  );
  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceItem[]>([]);
  const [selectedInvoicesForPayment, setSelectedInvoicesForPayment] = useState<
    InvoiceItem[]
  >([]);
  const [isReceivePaymentFormActive, setIsReceivePaymentFormActive] = useAtom(
    isReceivePaymentFormActiveAtom,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceOverviewForPayment, setInvoiceOverviewForPayment] =
    useState<InvoiceOverviewInfo>();
  const [invoiceFilters, setInvoiceFilters] = useState<InvoiceFilters>({
    fromDate: getDateRange("last12Months").startDate,
    toDate: getDateRange("last12Months").endDate,
    status:
      user?.roleCode === Role.Customer
        ? String(CUSTOMER_INVOICE_STATUS_FILTER_DROPDOWN[0].value)
        : InvoiceStatus.All,
    paymentStatus: "",
    sortBy: "invoiceDate",
    sortOrder: "desc",
  });

  const [toastOptions, setToastOptions] = useState<ToastOptions>({
    open: false,
    message: "",
    severity: "info",
  });
  const resetPage = () => {
    setInvoicePaginationModel({ ...invoicePaginationModel, page: 0 });
  };
  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const sortField = sortModel[0].field;
      const sortOrder: GridSortDirection = sortModel[0].sort;
      if (sortOrder) {
        setInvoiceFilters({
          ...invoiceFilters,
          sortBy: sortField,
          sortOrder: sortOrder,
        });
      }
    } else if (invoiceFilters.sortBy !== "") {
      setInvoiceFilters({
        ...invoiceFilters,
        sortBy: "",
        sortOrder: "",
      });
    }
  };
  const buildInvoiceRequestParams = (): InvoiceRequestParams => {
    return {
      orgId: selectedOrgId || 0,
      accountId:
        user?.roleCode === Role.SuperAdmin || user?.roleCode === Role.Admin
          ? invoiceFilters?.accountId
            ? invoiceFilters?.accountId || 0
            : selectedAccountId || 0
          : selectedAccountId || 0,
      filters: {
        page: invoiceFilters?.page ? invoiceFilters?.page + 1 : 1,
        pageSize: invoiceFilters?.pageSize,
        fromDate: invoiceFilters?.fromDate,
        toDate: invoiceFilters?.toDate,
        status:
          invoiceFilters?.status !== InvoiceStatus.All
            ? invoiceFilters?.status
              ? invoiceFilters?.status
              : ""
            : "",
        paymentStatus: invoiceFilters?.paymentStatus
          ? invoiceFilters?.paymentStatus
          : "",
        sortBy: invoiceFilters?.sortBy,
        sortOrder: invoiceFilters?.sortOrder,
      },
    };
  };

  const invoiceRequestParams = buildInvoiceRequestParams();

  const isAllowedToFetch =
    selectedOrgId &&
    !(user?.roleCode === Role.Customer && invoiceRequestParams.accountId === 0);

  const downloadInvoicesQuery = useFetchDownloadInvoicesCsvQuery(
    invoiceRequestParams,
    {
      enabled: false,
    },
  );
  const fetchInvoicesQuery = useFetchInvoicesQuery(
    invoiceRequestParams,
    isAllowedToFetch ? true : false,
  );
  const attachPdfToQuickbooksMutation = useAttachPdfToQuickbooksMutation();
  const regenerateInvoiceMutation = useRegenerateInvoiceMutation();
  const fetchInvoiceOverviewQuery = useFetchInvoiceOverviewQuery(
    selectedInvoices[0]?.orgId || 0,
    selectedInvoices[0]?.accountId || 0,
    selectedInvoices[0]?.invoiceNumber || "",
    {
      enabled:
        selectedInvoices[0]?.orgId &&
        selectedInvoices[0]?.accountId &&
        selectedInvoices[0]?.invoiceNumber &&
        selectedInvoices.length === 1 &&
        (selectedInvoices[0].invoiceStatus === InvoiceStatus.InProgress ||
          selectedInvoices[0].invoiceStatus === InvoiceStatus.SendToCustomer)
          ? true
          : false,
    },
  );

  const columns: GridColDef[] = [
    {
      field: "invoiceDate",
      headerName: "Date",
      width: 230,
      editable: false,
      valueGetter: (value) => {
        return formatDate(value, "dddd - MMMM DD,YYYY");
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 230,
      editable: false,
      valueGetter: (value) => {
        return formatDate(value, "dddd - MMMM DD,YYYY");
      },
    },
    {
      field: "accountName",
      headerName: "Account Name",
      width: 150,
      editable: false,
      sortable: false,
    },
    {
      field: "invoiceFrequency",
      headerName: "Frequency",
      width: 150,
      editable: false,
      sortable: false,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      width: 200,
      editable: false,
    },
    {
      field: "invoiceAmount",
      headerName: "Amount",
      width: 150,
      editable: false,
      valueFormatter: (value) => {
        return `${formatCurrency(value)}`;
      },
    },
    {
      field: "invoiceStatus",
      headerName: "Invoice Status",
      width: 150,
      editable: false,
      valueFormatter: ($, row) => {
        if (user?.roleCode === Role.Customer) {
          if (
            [
              InvoiceStatus.OnHold,
              InvoiceStatus.InProgress,
              InvoiceStatus.SendToCustomer,
            ].includes(row.invoiceStatus)
          ) {
            return "Unpaid";
          } else if (row.invoiceStatus === InvoiceStatus.PaymentReceived) {
            return "Paid";
          } else {
            return "Pending";
          }
        }

        return STATUS_LABELS[row.invoiceStatus];
      },
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 180,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const status: InvoicePaymentStatus =
          (row.paymentStatus as InvoicePaymentStatus) ??
          InvoicePaymentStatus.Unpaid;

        return (
          <span
            style={{
              color: PAYMENT_STATUS_COLORS[status],
            }}
          >
            {PAYMENT_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      field: "subStatus",
      headerName: "Sub Status",
      width: 150,
      editable: false,
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 300,
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <InvoiceActionIcons
          invoice={params.row}
          handleAttachPdf={handleAttachPdf}
          handleRegenerateInvoice={handleRegenerateInvoice}
          handleSelectForMultiplePayments={handleSelectForMultiplePayments}
          selectedInvoicesForPayment={selectedInvoicesForPayment}
        />
      ),
    },
  ];

  const handleRowSelectionChange = (rows: InvoiceItem[]) => {
    setSelectedInvoices(rows);
  };

  const handleRefresh = () => {
    isAllowedToFetch && fetchInvoicesQuery.refetch();
  };

  const handleDownload = () => {
    isAllowedToFetch && downloadInvoicesQuery.refetch();
  };

  const onCreateInvoice = () => {
    setIsCreateInvoiceModalOpen(true);
  };

  const handleCloseCreateInvoiceModal = () => {
    setIsCreateInvoiceModalOpen(false);
  };

  const handleAttachPdf = (invoice: InvoiceItem) => {
    attachPdfToQuickbooksMutation.mutate({
      orgId: invoice.orgId,
      accountId: invoice.accountId,
      invoiceNumber: invoice.invoiceNumber,
    });
  };

  const handleRegenerateInvoice = (invoice: InvoiceItem) => {
    setOrgId(invoice.orgId);
    setAccountId(invoice.accountId);
    regenerateInvoiceMutation.mutate({
      orgId: invoice.orgId,
      accountId: invoice.accountId,
      invoiceNumber: invoice.invoiceNumber,
    });
  };

  const handleSelectForMultiplePayments = (invoice: InvoiceItem) => {
    setSelectedInvoicesForPayment((prev) => [...prev, invoice]);
  };

  const handleClickInvoiceRow = ({ row }: { row: InvoiceItem }) => {
    if (user) {
      const tab = queryParams.get(ACCOUNT_DETAILS_TAB_KEY);
      const route = getInvoiceDetailsRoute(user);
      const path =
        (tab ? `${tab}/` : "") +
        route.path.replace(":invoiceNumber", row.invoiceNumber).concat(
          buildQueryString({
            orgId: row.orgId,
            accountId: row.accountId,
          }),
        );

      navigate(path);
    }
  };

  const handleNavigateToPayment = () => {
    if (user) {
      const tab = queryParams.get(ACCOUNT_DETAILS_TAB_KEY);
      const route = getMultipleCheckPaymentsRoute(user);
      const path = (tab ? `${tab}/` : "") + route.path;
      navigate(path, { state: { selectedInvoicesForPayment } });
    }
  };

  useEffect(() => {
    setSelectedInvoicesForPayment([]);
  }, [selectedOrgId]);

  const handleClosePaymentDialog = (message: InvoicePaymentCallbackMessage) => {
    setIsReceivePaymentFormActive(false);
    if (message === InvoicePaymentCallbackMessage.PaymentSuccess) {
      fetchInvoicesQuery.refetch();
      fetchInvoiceOverviewQuery.refetch();
    }
  };

  useEffect(() => {
    if (attachPdfToQuickbooksMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "PDF attached",
        severity: "success",
      });
      handleRefresh();
    }

    if (attachPdfToQuickbooksMutation.isError) {
      setToastOptions({
        open: true,
        message: "Failed to attach PDF",
        severity: "error",
      });
    }
  }, [
    attachPdfToQuickbooksMutation.isLoading,
    attachPdfToQuickbooksMutation.isSuccess,
    attachPdfToQuickbooksMutation.isError,
  ]);

  useEffect(() => {
    if (regenerateInvoiceMutation.isSuccess) {
      setRequestId(regenerateInvoiceMutation.data.data);
      setIsStatusModalOpen(true);
    }

    if (regenerateInvoiceMutation.isError) {
      setToastOptions({
        open: true,
        message: "Invoice regenerating failed!",
        severity: "error",
      });
    }
  }, [
    regenerateInvoiceMutation.isLoading,
    regenerateInvoiceMutation.isSuccess,
    regenerateInvoiceMutation.isError,
  ]);

  useEffect(() => {
    if (fetchInvoiceOverviewQuery.isSuccess) {
      setInvoiceOverviewForPayment(fetchInvoiceOverviewQuery.data);
    }
    setLoading(
      fetchInvoiceOverviewQuery.isLoading ||
        fetchInvoiceOverviewQuery.isFetching ||
        fetchInvoiceOverviewQuery.isRefetching,
    );
  }, [
    fetchInvoiceOverviewQuery.isSuccess,
    fetchInvoiceOverviewQuery.isError,
    fetchInvoiceOverviewQuery.data,
    fetchInvoiceOverviewQuery.error,
    fetchInvoiceOverviewQuery.isLoading,
    fetchInvoiceOverviewQuery.isFetching,
    fetchInvoiceOverviewQuery.isRefetching,
  ]);

  useEffect(() => {
    if (downloadInvoicesQuery.isSuccess) {
      const accountName = selectedAccount ? selectedAccount.accountName : "";
      const fileName =
        [
          accountName,
          invoiceFilters.fromDate || "",
          invoiceFilters.fromDate && invoiceFilters.toDate ? "to" : "",
          invoiceFilters.toDate || "",
        ]
          .filter(Boolean)
          .join("_") + "_invoice.csv";
      const blob = new Blob([downloadInvoicesQuery.data], {
        type: "text/csv",
      });
      const linkToCsv = URL.createObjectURL(blob);

      if (linkToCsv) downloadFile(linkToCsv, fileName);

      queryClient.resetQueries("downloadInvoices");
    }
  }, [
    downloadInvoicesQuery.isSuccess,
    downloadInvoicesQuery.data,
    downloadInvoicesQuery.error,
    downloadInvoicesQuery.isError,
    downloadInvoicesQuery.isRefetching,
  ]);

  useEffect(() => {
    if (fetchInvoicesQuery.isSuccess) {
      setInvoiceList(fetchInvoicesQuery.data);
    }
  }, [fetchInvoicesQuery.isSuccess, fetchInvoicesQuery.data]);

  useEffect(() => {
    setInvoiceFilters({
      ...invoiceFilters,
      page: invoicePaginationModel.page,
      pageSize: invoicePaginationModel.pageSize,
    });
  }, [invoicePaginationModel]);

  useEffect(() => {
    const dateRange = queryParams.get("dateRange");
    const dateFilters: Partial<InvoiceFilters> = {
      fromDate: queryParams.get("fromDate") || undefined,
      toDate: queryParams.get("toDate") || undefined,
    };

    if (
      dateRange &&
      Object.values(DateRangeOptions).includes(dateRange as DateRangeOptions)
    ) {
      if (dateRange !== DateRangeOptions.CustomDate) {
        dateFilters.fromDate = getDateRange(dateRange).startDate;
        dateFilters.toDate = getDateRange(dateRange).endDate;
      }
      dateFilters.dateRange = dateRange;

      setSelectedDateRange(dateRange);
    }

    const queryFilters: InvoiceFilters = {
      orgId: Number(queryParams.get("orgId")) || undefined,
      accountId: Number(queryParams.get("accountId")) || undefined,
      status:
        queryParams.get("status") ||
        (user?.roleCode === Role.Customer
          ? String(CUSTOMER_INVOICE_STATUS_FILTER_DROPDOWN[0].value)
          : InvoiceStatus.All),
      ...dateFilters,
      page: Number(queryParams.get("page")) || undefined,
      pageSize: Number(queryParams.get("pageSize")) || undefined,
      sortBy: queryParams.get("sortBy") || undefined,
      sortOrder: queryParams.get("sortOrder") || undefined,
    };

    const tempInvoiceFiltersFromQueryStrings = Object.entries(
      queryFilters,
    ).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const tempFilterValuesFromQueryStrings = Object.entries({
      orgId: tempInvoiceFiltersFromQueryStrings.orgId,
      accountId: tempInvoiceFiltersFromQueryStrings.accountId,
      status: tempInvoiceFiltersFromQueryStrings.status,
    }).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    Object.values(tempFilterValuesFromQueryStrings).length > 0 &&
      setFilterValues({
        ...filterValues,
        ...tempFilterValuesFromQueryStrings,
      });
    setInvoiceFilters({
      ...invoiceFilters,
      ...tempInvoiceFiltersFromQueryStrings,
    });
    setInvoicePaginationModel({
      ...invoicePaginationModel,
      page: tempInvoiceFiltersFromQueryStrings.page || 0,
      pageSize: tempInvoiceFiltersFromQueryStrings.pageSize || 10,
    });
  }, []);

  useEffect(() => {
    const filters = Object.entries(invoiceFilters).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    const tab = queryParams.get("tab");

    setQueryParams({
      ...(tab && { tab }),
      ...filters,
    });
  }, [invoiceFilters]);

  useEffect(() => {
    if (!invoiceList?.items) return;

    if (selectedInvoices.length > 0) {
      const updatedSelectedInvoice = selectedInvoices.map(
        (selectedInvoice) =>
          invoiceList.items.find(
            (invoice) => invoice.id === selectedInvoice.id,
          ) || selectedInvoice,
      );

      setSelectedInvoices(updatedSelectedInvoice);
    }
  }, [invoiceList?.items]);

  return (
    <>
      <Box
        sx={{
          marginTop: "50px",
          width: "100%",
          boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
          borderRadius: "10px",
        }}
      >
        <Toast setOptions={setToastOptions} options={toastOptions} />
        <Box>
          <Grid2
            container
            direction={"column"}
            sx={{
              marginTop: "0px",
              marginBottom: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: "10px",
            }}
          >
            <InvoiceToolbar
              invoices={selectedInvoices}
              invoiceFilters={invoiceFilters}
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={setSelectedDateRange}
              setInvoiceFilters={setInvoiceFilters}
              onRefresh={handleRefresh}
              onDownload={handleDownload}
              onCreateInvoice={onCreateInvoice}
              resetPage={resetPage}
            />
          </Grid2>
          <Grid2 container direction={"column"} spacing={0}>
            <Box
              sx={{
                display: {
                  xs: showMobilePaymentStatusMenu ? "flex" : "none",
                  md: "flex",
                },
                justifyContent: {
                  xs: "center",
                  md: "flex-end",
                },
                px: 2,
                pt: 1,
                pb: 1,
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                onClick={() => {
                  resetPage();

                  setInvoiceFilters({
                    ...invoiceFilters,
                    paymentStatus: "",
                  });

                  if (showMobilePaymentStatusMenu) {
                    setShowMobilePaymentStatusMenu(false);
                  }
                }}
              >
                Show All Invoices
              </Typography>
            </Box>
            <Box
              sx={{
                display: {
                  xs: showMobilePaymentStatusMenu ? "flex" : "none",
                  md: "flex",
                },
                flexDirection: { xs: "column", md: "row" },
                flexWrap: { xs: "nowrap", md: "wrap" },
                gap: 1,
                px: 2,
                py: 1,
                width: "100%",
              }}
            >
              {PAYMENT_STATUS_TILES.map((tile) => {
                const data = invoiceList?.paymentTotals?.[tile.key];

                return (
                  <Box
                    key={tile.key}
                    onClick={() => {
                      resetPage();

                      setInvoiceFilters({
                        ...invoiceFilters,
                        paymentStatus: tile.key,
                      });

                      if (showMobilePaymentStatusMenu) {
                        setShowMobilePaymentStatusMenu(false);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      flex: {
                        md: "1 1 calc(50% - 8px)",
                        lg: "1 1 calc(25% - 8px)",
                      },
                      minWidth: {
                        xs: "auto",
                        md: "calc(50% - 8px)",
                        lg: "calc(25% - 8px)",
                      },
                      maxWidth: {
                        md: "calc(50% - 8px)",
                        lg: "calc(25% - 8px)",
                      },
                      px: 2,
                      py: 1,
                      borderRadius: "10px",
                      backgroundColor:
                        invoiceFilters.paymentStatus === tile.key
                          ? "#d8e5f2bb"
                          : "#fff",
                      border: "1px solid #E5E7EB",
                      borderTop: `3px solid ${tile.color}`,
                      boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {tile.title}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          {data?.count}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "#6b7280",
                          }}
                        >
                          invoices
                        </Typography>
                      </Box>

                      <Box textAlign="right">
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: tile.color,
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(data?.amount || 0)}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          total
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: {
                  xs: showMobilePaymentStatusMenu ? "none" : "contents",
                  md: "contents",
                },
              }}
            >
              {!showMobilePaymentStatusMenu && (
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" },
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setShowMobilePaymentStatusMenu(true);
                    }}
                  >
                    ← Back
                  </Typography>
                </Box>
              )}
              <DataGrid
                onRowClick={handleClickInvoiceRow}
                columns={columns}
                rows={invoiceList?.items || []}
                loading={
                  fetchInvoicesQuery.isLoading ||
                  fetchInvoicesQuery.isRefetching ||
                  downloadInvoicesQuery.isLoading ||
                  downloadInvoicesQuery.isRefetching ||
                  attachPdfToQuickbooksMutation.isLoading ||
                  regenerateInvoiceMutation.isLoading ||
                  isInvoiceActionsMutationsLoading
                }
                sortModel={[
                  {
                    field: invoiceFilters.sortBy || "",
                    sort:
                      (invoiceFilters.sortOrder as GridSortDirection) || null,
                  },
                ]}
                onSortModelChange={handleSortModelChange}
                paginationModel={invoicePaginationModel}
                setPaginationModel={setInvoicePaginationModel}
                onSelectedRowsChange={handleRowSelectionChange}
                totalRecords={invoiceList?.totalItems}
                paginationMode="server"
                sortingMode="server"
                rowCount={invoiceList?.totalItems ? invoiceList?.totalItems : 0}
                pageSize={invoiceList?.pageSize}
                sx={{
                  border: "none",
                  backgroundColor: theme.palette.secondary.light,
                  padding: "20px",
                  paddingTop: "0px",
                  "& .MuiSvgIcon-root": {
                    fontSize: 16,
                  },
                  borderBottomLeftRadius: "10px",
                  borderBottomRightRadius: "10px",
                  height: "70vh",
                }}
                footerProps={{
                  addBtnLabel:
                    user?.roleCode !== Role.Customer ? "Pay with check" : "",
                  hasAddIcon: true,
                  customAddIcon: PaymentIcon,
                  addBtnBadgeContent: selectedInvoicesForPayment.length,
                  disableAddBtn:
                    selectedInvoicesForPayment.length === 0 ||
                    fetchInvoicesQuery.isLoading,
                  onAddButtonClick: handleNavigateToPayment,
                }}
              />
            </Box>
          </Grid2>
        </Box>
      </Box>
      <Modal
        open={isCreateInvoiceModalOpen}
        onClose={handleCloseCreateInvoiceModal}
        title={"Create Invoice"}
        content={
          <CreateInvoice
            invoiceFilters={invoiceFilters}
            handleRefresh={handleRefresh}
          />
        }
        sx={{
          modal: {
            width: { lg: "80%", md: "85%", xs: "80%", sm: "80%" },
            height: { lg: "85%", md: "85%", xs: "85%", sm: "85%" },
          },
        }}
      />
      <CreateRegenerateInvoiceModal
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title={"Regenerate Invoice"}
        requestId={requestId}
        orgId={orgId}
        accountId={accountId}
        regenerate={true}
        handleRefresh={handleRefresh}
      />
      <PaymentDialog
        open={isReceivePaymentFormActive}
        loading={loading}
        handleClose={handleClosePaymentDialog}
        invoiceOverview={invoiceOverviewForPayment}
      />
    </>
  );
};
